<script>
  // Tag-style multi-pick with search, for audiences/categories/adventures/etc.
  // Static mode: `options` ([{ value, label }] or plain strings, filtered client-side).
  // Async mode: `source` (an AdminSite::OptionsProp source key) + optional `sourceParams`
  //   — options are searched + paginated server-side; pass `selected` ([{ value, label }])
  //   so the chips for already-saved ids have labels without an extra fetch.
  // `onadd(option)` / `onremove(value)` fire on changes; `option` carries the full server
  // payload in async mode (callers like the campaigns form seed content cards from it).
  import Icon from './ui/icon.svelte'
  import Button from './ui/button.svelte'
  import { loadSelectOptions } from '../lib/shared_props.js'

  let {
    value = $bindable([]),
    options = [],
    source = null,
    sourceParams = {},
    selected = [],
    label,
    placeholder = 'Search…',
    help,
    onadd = null,
    onremove = null
  } = $props()

  const listboxId = $props.id()

  let query = $state('')
  let open = $state(false)
  let highlighted = $state(0)
  let items = $state([])
  let pagy = $state(null)
  let loading = $state(false)
  let loadFailed = $state(false)
  let chosen = $state(Object.fromEntries((selected ?? []).map((option) => [option.value, option])))
  let root = $state(null)

  // Options added later (e.g. a record created in a drawer) bring their own label.
  $effect(() => {
    for (const option of selected ?? []) if (!chosen[option.value]) chosen[option.value] = option
  })

  let requestId = 0
  let fetchedKey = null
  let hydrated = false

  const normalizedOptions = $derived(
    options.map((option) =>
      typeof option === 'object' && option !== null ? option : { value: option, label: option }
    )
  )

  const selectedOptions = $derived(
    value
      .map((id) => chosen[id] ?? normalizedOptions.find((option) => option.value === id))
      .filter(Boolean)
  )

  const availableOptions = $derived(
    (source ? items : normalizedOptions.filter((option) => String(option.label).toLowerCase().includes(query.trim().toLowerCase())))
      .filter((option) => !value.includes(option.value))
  )

  const hasMore = $derived(!!source && !!pagy && pagy.pages > pagy.page)
  const paramsKey = $derived(JSON.stringify(sourceParams ?? {}))

  // Async mode: hydrate labels for saved ids the caller didn't pass via `selected`.
  // No sourceParams: saved ids may fall outside the current filters and still need labels.
  $effect(() => {
    if (!source || hydrated) return
    const missing = value.filter((id) => !chosen[id])
    hydrated = true
    if (missing.length === 0) return
    loadSelectOptions(source, { ids: missing })
      .then((data) => {
        data.options.forEach((option) => {
          chosen[option.value] = option
        })
      })
      .catch(() => {})
  })

  const load = (pageNumber, append = false) => {
    const rid = ++requestId
    loading = true
    loadFailed = false
    loadSelectOptions(source, { q: query, page: pageNumber, params: sourceParams ?? {} })
      .then((data) => {
        if (rid !== requestId) return
        // Dedupe on append: a record created between page fetches shifts pagination
        // and could repeat an option (duplicate {#each} keys crash).
        items = append
          ? [...items, ...data.options.filter((option) => !items.some((item) => item.value === option.value))]
          : data.options
        pagy = data.pagy
        loading = false
      })
      .catch(() => {
        if (rid !== requestId) return
        loading = false
        loadFailed = true
      })
  }

  $effect(() => {
    if (!source || !open) return
    const key = `${query.trim()}|${paramsKey}`
    if (key === fetchedKey) return
    const timeoutId = setTimeout(
      () => {
        fetchedKey = key
        load(1)
      },
      fetchedKey === null ? 0 : 250
    )
    return () => clearTimeout(timeoutId)
  })

  const openPanel = () => {
    open = true
    highlighted = 0
    if (source) fetchedKey = null
  }

  const add = (option) => {
    value = [...value, option.value]
    chosen[option.value] = option
    query = ''
    highlighted = 0
    onadd?.(option)
  }

  const remove = (optionValue) => {
    value = value.filter((id) => id !== optionValue)
    onremove?.(optionValue)
  }

  const onkeydown = (event) => {
    if (event.key === 'Escape') {
      open = false
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) openPanel()
      else highlighted = Math.min(highlighted + 1, availableOptions.length - 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      highlighted = Math.max(highlighted - 1, 0)
    } else if (event.key === 'Enter') {
      if (!open) return
      event.preventDefault()
      const option = availableOptions[highlighted]
      if (option) add(option)
    }
  }

  $effect(() => {
    if (!open || highlighted < 0) return
    root?.querySelector('[data-highlighted="true"]')?.scrollIntoView({ block: 'nearest' })
  })
</script>

<svelte:document
  onpointerdown={(event) => {
    if (open && root && !root.contains(event.target)) open = false
  }}
/>

<fieldset class="fieldset">
  {#if label}<legend class="fieldset-legend">{label}</legend>{/if}

  {#if selectedOptions.length > 0}
    <div class="flex flex-wrap gap-2 mb-2">
      {#each selectedOptions as option (option.value)}
        <span class="badge badge-primary gap-1">
          {option.label}
          <button type="button" aria-label="Remove {option.label}" onclick={() => remove(option.value)}>
            <Icon name="close" size="xs" />
          </button>
        </span>
      {/each}
    </div>
  {/if}

  <div class="dropdown {open ? 'dropdown-open' : ''} w-full" bind:this={root}>
    <input
      type="text"
      bind:value={query}
      {placeholder}
      class="input input-sm w-full pr-7"
      role="combobox"
      aria-controls={listboxId}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-label={label ?? placeholder}
      onfocus={openPanel}
      oninput={() => {
        if (!open) openPanel()
        highlighted = 0
      }}
      {onkeydown}
    />
    {#if query}
      <button
        type="button"
        class="absolute right-1.5 top-4 -translate-y-1/2 text-base-content/40 hover:text-base-content cursor-pointer"
        aria-label="Clear search"
        onclick={() => {
          query = ''
          highlighted = 0
        }}
      >
        <Icon name="close" size="xs" />
      </button>
    {/if}
    {#if open}
      <div class="dropdown-content bg-base-100 border border-base-300 rounded-box shadow-lg z-30 mt-1 w-full p-2 flex flex-col gap-1">
        <ul id={listboxId} class="menu menu-sm w-full max-h-48 overflow-y-auto flex-nowrap p-0" role="listbox">
          {#each availableOptions as option, index (option.value)}
            <li>
              <button
                type="button"
                role="option"
                aria-selected="false"
                class={highlighted === index ? 'bg-primary/10 text-primary' : ''}
                data-highlighted={highlighted === index}
                onclick={() => {
                  // a click pick closes the panel (old CMS react-select), so it never sits over
                  // the next field; a click in the box reopens it for another pick
                  add(option)
                  open = false
                }}
              >
                <span class="truncate">{option.label}</span>
              </button>
            </li>
          {/each}
          {#if !loading && availableOptions.length === 0}
            <li class="px-3 py-2 text-sm text-base-content/60">
              {loadFailed ? 'Failed to load options.' : 'No matches.'}
            </li>
          {/if}
        </ul>
        {#if loading}
          <div class="flex justify-center py-1"><span class="loading loading-spinner loading-xs"></span></div>
        {:else if hasMore}
          <Button variant="ghost" size="xs" onclick={() => load(pagy.page + 1, true)}>
            Load more ({items.length} of {pagy.count})
          </Button>
        {/if}
      </div>
    {/if}
  </div>

  {#if help}<p class="label whitespace-normal">{help}</p>{/if}
</fieldset>
